import { useEffect, useState } from 'react'
import { User, Mail, Camera, Save, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/stores/auth-store'

export function ProfileSettings() {
  const { user, updateProfile } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    name: user?.name || '',
    display_name: user?.display_name || '',
    email: user?.email || '',
    avatar_url: user?.avatar_url || '',
  })

  useEffect(() => {
    setFormData({
      name: user?.name || '',
      display_name: user?.display_name || '',
      email: user?.email || '',
      avatar_url: user?.avatar_url || '',
    })
  }, [user])

  const handleInputChange = (field: string, value: string) => {
    setFormData((previous) => ({ ...previous, [field]: value }))
    setSuccess(false)
    setError(null)
  }

  const handleSave = async () => {
    if (!user) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const success = await updateProfile(formData)
      if (!success) {
        throw new Error('Profiel bijwerken mislukt')
      }

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Er is een fout opgetreden')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <User className="h-5 w-5 mr-2" />
          Profiel Instellingen
        </CardTitle>
        <p className="text-sm text-gray-600">Beheer je basisgegevens voor KookInspiratie.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
            {formData.avatar_url ? (
              <img src={formData.avatar_url} alt="Profiel foto" className="w-16 h-16 object-cover" />
            ) : (
              <User className="h-8 w-8 text-gray-500" />
            )}
          </div>
          <Button variant="outline" size="sm" disabled>
            <Camera className="h-4 w-4 mr-2" />
            Avatar via URL
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="inline h-4 w-4 mr-1" />
              Volledige Naam
            </label>
            <Input
              value={formData.name}
              onChange={(event) => handleInputChange('name', event.target.value)}
              placeholder="Je volledige naam"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="inline h-4 w-4 mr-1" />
              Weergavenaam
            </label>
            <Input
              value={formData.display_name}
              onChange={(event) => handleInputChange('display_name', event.target.value)}
              placeholder="Naam zoals anderen je zien"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Mail className="inline h-4 w-4 mr-1" />
            E-mailadres
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={(event) => handleInputChange('email', event.target.value)}
            placeholder="je@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Avatar URL</label>
          <Input
            value={formData.avatar_url}
            onChange={(event) => handleInputChange('avatar_url', event.target.value)}
            placeholder="https://..."
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-600 text-sm">Profiel succesvol bijgewerkt.</p>
          </div>
        )}

        <div className="pt-4 border-t">
          <Button onClick={handleSave} disabled={loading} className="w-full md:w-auto">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Opslaan...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Profiel Opslaan
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
